import styled from "@emotion/styled";
import * as React from "react";
import { Field, Form, useForm } from "react-final-form";

import {
  AutoComplete,
  Button,
  FormField,
  Input,
  Modal,
  ModalPortal,
  SpacingToken,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { isEmail } from "helpers/validate";
import { useDebouncedAddresses } from "hooks/useDebounceGetAddresses";
import { ICc } from "interfaces/entities/ticket";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

interface IProps {
  requesterName?: string;
  requesterEmail: string;
  ccs: ICc[] | null;
  onClose(): void;
  onSubmit(requesterName: string, requesterEmail: string): void;
}

interface IFormValues {
  requesterName: string;
  requesterEmail: string;
}

function validateEmail(email: string, ccs: ICc[] | null): string | undefined {
  if (!isEmail(email)) {
    return "Email is incorrect";
  }
  if (ccs?.find(cc => email === cc.email)) {
    return "This person is already in the loop";
  }
  return undefined;
}

function isRequesterNameValid(requesterName: string | undefined): boolean {
  return !!requesterName && requesterName.trim().length > 0;
}

export const ChangeTicketRequesterModal: React.FC<IProps> = ({
  requesterName = "",
  requesterEmail,
  ccs,
  onClose,
  onSubmit,
}) => {
  const firstInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  const handleSubmit = (values: IFormValues): void => {
    onSubmit(values.requesterName.trim(), values.requesterEmail.trim());
  };

  const validate = (values: IFormValues): Partial<IFormValues> => {
    const errors: Partial<IFormValues> = {};
    if (!isRequesterNameValid(values.requesterName)) {
      errors.requesterName = "Name is required";
    }
    const emailError = validateEmail(values.requesterEmail, ccs);
    if (emailError) {
      errors.requesterEmail = emailError;
    }
    return errors;
  };

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <Form<IFormValues>
        onSubmit={handleSubmit}
        validate={validate}
        initialValues={{ requesterName, requesterEmail }}
        keepDirtyOnReinitialize
        render={({ handleSubmit, valid }) => (
          <form onSubmit={handleSubmit}>
            <Modal
              heading="Change requester"
              onClose={onClose}
              footer={
                <Flex gap={withVar(SpacingToken.Spacing2)}>
                  <Button size="large" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button size="large" kind="primary" type="submit" disabled={!valid}>
                    Save
                  </Button>
                </Flex>
              }
            >
              <StyledForm>
                <Field name="requesterName">
                  {({ input, meta }) => (
                    <InputWrapper labelText="Name" error={meta.touched && meta.error}>
                      <Input
                        {...input}
                        ref={firstInputRef}
                        maxLength={100}
                        id="requester-name"
                        className="fs-mask"
                        placeholder="Name…"
                        data-1p-ignore
                        error={meta.touched && meta.error}
                      />
                    </InputWrapper>
                  )}
                </Field>
                <RequesterEmailField ccs={ccs} />
              </StyledForm>
            </Modal>
          </form>
        )}
      />
    </ModalPortal>
  );
};

const RequesterEmailField: React.FC<{
  ccs: ICc[] | null;
}> = ({ ccs }) => {
  const form = useForm();
  return (
    <Field<string> name="requesterEmail" validate={(email: string) => validateEmail(email, ccs)}>
      {({ input, meta }) => {
        const addresses = useDebouncedAddresses("requester", input.value);
        const filteredAddresses = addresses?.filter(address => !validateEmail(address, ccs));
        return (
          <InputWrapper labelText="Email" error={meta.touched && meta.error}>
            <AutoComplete
              options={filteredAddresses}
              id="requester-email"
              maxLength={1000}
              error={meta.touched && meta.error}
              placeholder="Email…"
              data-1p-ignore
              autoComplete="off"
              {...input}
              onChange={e => {
                input.onChange(e);
                form.resetFieldState("requesterEmail");
              }}
            />
          </InputWrapper>
        );
      }}
    </Field>
  );
};

const InputWrapper = styled(FormField)`
  width: 360px;
  max-width: 100%;
`;

const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(${SpacingToken.Spacing2});
`;
